var _Object$getOwnPropert;
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { Button } from "../components/Button";
import { Chip } from "../components/Chip";
import { Icon } from "../components/Icon";
import { KeyboardShortcut } from "../components/KeyboardShortcut";
import { LinkWrapper } from "../components/LinkWrapper";
import { radioIndicatorStyles, radioStyles } from "../components/RadioGroup";
import { ScrollArea } from "../components/ScrollArea";
import { SearchInput } from "../components/SearchInput";
import { Tooltip } from "../components/Tooltip";
import { useSheetContainer } from "../hooks/useSheetContainer";
import { Check, ChevronRight } from "../icons/v2-stroke";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import * as React from "react";
import { useMemo, useRef } from "react";
const ITEM_VARIANTS = ["default", "warning"];
export const menuStyleClasses = {
  inset: "pl-8",
  container: cn("rounded-xl border-hovering", "border border-border", "bg-overlay-background", "text-foreground", "z-50 min-w-[8rem]", "origin-[var(--radix-dropdown-menu-content-transform-origin)]"),
  // Enter/exit animation applied to the top-level dropdown content only. Nested
  // sub-menus open instantly so they don't feel sluggish when drilling in.
  containerAnimation: cn("duration-200 ease-enter data-[state=closed]:duration-150 motion-reduce:animate-none", "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2"),
  item: cva(cn("relative flex gap-2 cursor-pointer select-none items-center outline-hidden rounded-lg heading-sm transition-colors duration-150 motion-reduce:transition-none data-[disabled]:pointer-events-none", "data-[disabled]:text-primary-400"), {
    variants: {
      variant: {
        default: cn("p-2", "text-muted-foreground", "hover:bg-hover hover:text-foreground", "focus:text-foreground", "focus:bg-hover"),
        tags: cn("p-0.5", "text-muted-foreground", "hover:bg-hover hover:text-foreground", "focus:text-foreground", "focus:bg-hover"),
        warning: cn("p-2", "text-warning-600", "hover:bg-warning-50", "focus:bg-warning-50", "active:bg-warning-100")
      }
    },
    defaultVariants: {
      variant: "default"
    }
  }),
  subTrigger: {
    default: cn("mr-1 ml-auto tracking-widest", "text-primary-400"),
    span: "absolute left-2 flex h-3.5 w-3.5 items-center justify-center"
  },
  label: cn("px-2 py-2 heading-xs", "text-muted-foreground"),
  description: cn("grow truncate text-xs font-normal", "text-muted-foreground"),
  separator: cn("-mx-1 my-1 h-px", "bg-separator"),
  shortcut: "ml-auto"
};

/**
 * A menu of actions or options revealed from a trigger — Sparkle's action or
 * overflow menu, built on Radix DropdownMenu. Compose it from
 * DropdownMenuTrigger and DropdownMenuContent, with items, checkbox/radio
 * items, groups, submenus, a searchbar, and tag lists. Use it for secondary
 * or overflow actions, or to pick one value from a longer list than a
 * RadioGroup comfortably shows.
 * DropdownMenuContent defaults preventAutoFocusOnClose to true; set it to false
 * to return focus to a trigger. For controlled surfaces without a trigger,
 * onCloseAutoFocus can restore the opening control explicitly.
 * @summary Action/overflow menu opened from a trigger.
 */
const DropdownMenu = DropdownMenuPrimitive.Root;
/** Groups related menu items. */
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
/** Portals menu content to another part of the DOM. */
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
/** Root of a nested submenu (pair with DropdownMenuSubTrigger and DropdownMenuSubContent). */
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
/** Groups DropdownMenuRadioItems into a single-choice set. */
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;
/** Element that opens the menu (wrap a Button with asChild). */
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownItemRegistryContext = React.createContext(null);
const renderIcon = (icon, size = "xs", variant) => {
  // If it's a React element (already rendered), return it as is
  if (React.isValidElement(icon)) {
    return icon;
  }

  // For any component type (including exotic components), render it with Icon
  if (typeof icon === "function" || typeof icon === "object") {
    return /*#__PURE__*/React.createElement(Icon, {
      size: size,
      visual: icon,
      className: variant === "warning" ? undefined : "text-muted-foreground"
    });
  }

  // For primitive values, return null
  return null;
};
const ItemWithLabelIconAndDescription = ({
  label,
  icon,
  description,
  truncate,
  children,
  endComponent,
  variant
}) => {
  return /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement("div", {
    className: cn("grid flex-grow items-center gap-x-2.5", icon && endComponent ? "grid-cols-[auto_1fr_auto]" : icon ? "grid-cols-[auto_1fr]" : endComponent ? "grid-cols-[1fr_auto]" : "grid-cols-[1fr]")
  }, renderIcon(icon, "sm", variant), /*#__PURE__*/React.createElement("div", {
    className: cn("flex flex-col", truncate && "truncate")
  }, /*#__PURE__*/React.createElement("span", {
    className: cn(truncate ? "truncate" : "line-clamp-3")
  }, label), description && /*#__PURE__*/React.createElement("span", {
    className: cn("text-xs font-normal text-muted-foreground", truncate ? "truncate" : "line-clamp-3")
  }, description)), endComponent), children);
};

/** Menu item that opens a nested submenu, with a trailing chevron. */
const DropdownMenuSubTrigger = React.forwardRef(({
  className,
  label,
  icon,
  children,
  inset,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DropdownMenuPrimitive.SubTrigger, {
  ref: ref,
  className: cn(menuStyleClasses.item({
    variant: "default"
  }),
  // Keep the trigger highlighted while its sub-menu is open, so the
  // hover state doesn't drop when the pointer moves into the sub-menu.
  "data-[state=open]:bg-hover data-[state=open]:text-foreground", inset ? menuStyleClasses.inset : "", className),
  ...props
}, /*#__PURE__*/React.createElement(ItemWithLabelIconAndDescription, {
  label: label,
  icon: icon,
  endComponent: /*#__PURE__*/React.createElement(Icon, {
    size: "xs",
    visual: ChevronRight,
    className: "text-muted-foreground"
  })
}, children)));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;
/** Scrollable panel of a nested submenu; always portaled to keep its elevation shadow. */
const DropdownMenuSubContent = React.forwardRef(({
  className,
  children,
  dropdownHeaders,
  ...props
}, ref) => {
  const container = useSheetContainer();

  // Always portal: a sub-menu rendered inline sits inside the parent menu's
  // bg-overlay-background, where the nested-same-surface rule would strip its
  // elevation shadow. Wrapping in an extra DropdownMenuPortal at the call
  // site remains harmless.
  return /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Portal, {
    container: container
  }, /*#__PURE__*/React.createElement(DropdownMenuPrimitive.SubContent, {
    ref: ref,
    className: cn(menuStyleClasses.container, "flex flex-col", dropdownHeaders && "h-80 xs:h-96", className),
    ...props
  }, dropdownHeaders && /*#__PURE__*/React.createElement("div", {
    className: "sticky top-0 px-1 pt-1"
  }, dropdownHeaders), /*#__PURE__*/React.createElement(ScrollArea, {
    className: "w-full flex-1",
    hideScrollBar: false,
    orientation: "vertical",
    viewportClassName: cn("flex-1 p-1", "max-h-[calc(var(--radix-dropdown-menu-content-available-height)-var(--header-height,20px))]")
  }, children)));
});
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;
const nativeInputValueSetter = typeof window !== "undefined" ? (_Object$getOwnPropert = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")) === null || _Object$getOwnPropert === void 0 ? void 0 : _Object$getOwnPropert.set : undefined;
const SEARCHABLE_MENU_ITEM_SELECTOR = '[role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]';
function getFirstDropdownMenuItem(container) {
  return container.querySelector(SEARCHABLE_MENU_ITEM_SELECTOR);
}
function getDropdownSearchInput(container) {
  var _container$querySelec;
  return (_container$querySelec = container.querySelector('[data-dropdown-searchbar] input[type="text"]')) !== null && _container$querySelec !== void 0 ? _container$querySelec : container.querySelector('input[type="text"]');
}
function resolveDropdownSearchInput(searchInputRef, container) {
  var _searchInputRef$curre;
  return (_searchInputRef$curre = searchInputRef === null || searchInputRef === void 0 ? void 0 : searchInputRef.current) !== null && _searchInputRef$curre !== void 0 ? _searchInputRef$curre : getDropdownSearchInput(container);
}
function isDropdownTextEntryElement(element) {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement;
}
function setDropdownSearchInputValue(input, nextValue) {
  if (!nativeInputValueSetter) {
    return;
  }

  // Use the native setter so React sees the value change before the input event.
  nativeInputValueSetter.call(input, nextValue);
  input.dispatchEvent(new Event("input", {
    bubbles: true
  }));
}
/** The menu panel: portaled, animated, scrollable, with typed characters routed to an embedded searchbar. */
const DropdownMenuContent = React.forwardRef(({
  className,
  sideOffset = 4,
  mountPortal = true,
  mountPortalContainer,
  dropdownHeaders,
  highlightedItemId,
  preventAutoFocusOnClose = true,
  onCloseAutoFocus,
  onKeyDownCapture,
  onKeyDown,
  searchInputRef,
  viewportRef,
  scrollHighlightedItemIntoView = false,
  children,
  ...props
}, ref) => {
  const itemElementsRef = useRef(new Map());
  const handleKeyDownCapture = e => {
    onKeyDownCapture === null || onKeyDownCapture === void 0 || onKeyDownCapture(e);
    if (e.defaultPrevented) {
      return;
    }
    if (e.key !== "ArrowUp") {
      return;
    }
    const input = resolveDropdownSearchInput(searchInputRef, e.currentTarget);
    const firstItem = getFirstDropdownMenuItem(e.currentTarget);
    if (!input || !firstItem || document.activeElement !== firstItem) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    input.focus();
  };
  const handleKeyDown = e => {
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
    if (e.defaultPrevented) {
      return;
    }
    if (isDropdownTextEntryElement(document.activeElement)) {
      return;
    }
    const input = resolveDropdownSearchInput(searchInputRef, e.currentTarget);
    if (!input) {
      return;
    }

    // Treat single-character keys as search input
    if (e.key.length === 1) {
      e.preventDefault();
      input.focus();
      setDropdownSearchInputValue(input, input.value + e.key);
    } else if (e.key === "Backspace" && input.value.length > 0) {
      e.preventDefault();
      input.focus();
      setDropdownSearchInputValue(input, input.value.slice(0, -1));
    }
  };
  const handleCloseAutoFocus = React.useCallback(event => {
    if (preventAutoFocusOnClose) {
      event.preventDefault();
    }
    onCloseAutoFocus === null || onCloseAutoFocus === void 0 || onCloseAutoFocus(event);
  }, [preventAutoFocusOnClose, onCloseAutoFocus]);
  const registerItem = React.useCallback((itemId, element) => {
    if (element) {
      itemElementsRef.current.set(itemId, element);
    } else {
      itemElementsRef.current.delete(itemId);
    }
  }, []);
  const itemRegistryContextValue = useMemo(() => ({
    registerItem
  }), [registerItem]);
  React.useEffect(() => {
    if (!scrollHighlightedItemIntoView || !highlightedItemId) {
      return;
    }
    const highlightedItem = itemElementsRef.current.get(highlightedItemId);
    highlightedItem === null || highlightedItem === void 0 || highlightedItem.scrollIntoView({
      block: "nearest"
    });
  }, [highlightedItemId, scrollHighlightedItemIntoView]);
  const content = /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Content, {
    ref: ref,
    sideOffset: sideOffset,
    onKeyDownCapture: handleKeyDownCapture,
    onKeyDown: handleKeyDown,
    className: cn(menuStyleClasses.container, menuStyleClasses.containerAnimation, "flex flex-col", dropdownHeaders && "h-80 xs:h-96",
    // We use dropdownHeaders for putting search bar, so we can set the height for the container
    className),
    onCloseAutoFocus: handleCloseAutoFocus,
    ...props
  }, /*#__PURE__*/React.createElement("div", {
    className: cn("sticky top-0", dropdownHeaders && "px-1 pt-1")
  }, dropdownHeaders && dropdownHeaders), /*#__PURE__*/React.createElement(ScrollArea, {
    className: "w-full flex-1",
    viewportClassName: cn("flex-1 p-1", "max-h-[calc(var(--radix-dropdown-menu-content-available-height)-var(--header-height,20px))]"),
    viewportRef: viewportRef
  }, /*#__PURE__*/React.createElement(DropdownItemRegistryContext.Provider, {
    value: itemRegistryContextValue
  }, children)));
  const container = useSheetContainer(mountPortalContainer);
  return mountPortal ? /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Portal, {
    container: container
  }, content) : content;
});
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;
/** Standard menu item with label, leading icon, description, and optional link behavior. */
const DropdownMenuItem = React.forwardRef(({
  children,
  variant,
  description,
  className,
  inset,
  icon,
  itemId,
  truncateText,
  label,
  href,
  target,
  rel,
  asChild,
  replace,
  shallow,
  prefetch,
  endComponent,
  tooltip,
  ...props
}, ref) => {
  const dropdownItemRegistry = React.useContext(DropdownItemRegistryContext);
  const handleItemRef = React.useCallback(element => {
    if (typeof ref === "function") {
      ref(element);
    } else if (ref) {
      ref.current = element;
    }
    if (itemId) {
      dropdownItemRegistry === null || dropdownItemRegistry === void 0 || dropdownItemRegistry.registerItem(itemId, element);
    }
  }, [dropdownItemRegistry, itemId, ref]);
  const item = /*#__PURE__*/React.createElement(LinkWrapper, {
    href: href,
    target: target,
    rel: rel,
    replace: replace,
    shallow: shallow,
    prefetch: prefetch
  }, /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Item, {
    ref: handleItemRef,
    className: cn(menuStyleClasses.item({
      variant
    }), inset ? menuStyleClasses.inset : "", className),
    ...props,
    asChild: asChild
  }, /*#__PURE__*/React.createElement("div", {
    className: "h-full w-full"
  }, /*#__PURE__*/React.createElement(ItemWithLabelIconAndDescription, {
    label: label,
    icon: icon,
    description: description,
    truncate: truncateText,
    endComponent: endComponent,
    variant: variant
  }, children))));
  const itemWithTooltip = tooltip ? /*#__PURE__*/React.createElement(Tooltip, {
    tooltipTriggerAsChild: true,
    label: tooltip,
    trigger: /*#__PURE__*/React.createElement("span", {
      className: "block w-full"
    }, item)
  }) : item;
  return itemWithTooltip;
});
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;
/** Menu item with a leading check indicator, for multi-select options. */
const DropdownMenuCheckboxItem = React.forwardRef(({
  className,
  children,
  description,
  label,
  icon,
  truncateText,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DropdownMenuPrimitive.CheckboxItem, {
  ref: ref,
  className: cn(menuStyleClasses.item({
    variant: "default"
  }), menuStyleClasses.inset),
  ...props
}, /*#__PURE__*/React.createElement("span", {
  className: menuStyleClasses.subTrigger.span
}, /*#__PURE__*/React.createElement(DropdownMenuPrimitive.ItemIndicator, null, /*#__PURE__*/React.createElement(Icon, {
  size: "xs",
  visual: Check,
  className: "text-muted-foreground"
}))), /*#__PURE__*/React.createElement(ItemWithLabelIconAndDescription, {
  label: label,
  icon: icon,
  description: description,
  truncate: truncateText
}, children)));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

/** Menu item with a radio indicator, for single-choice selection inside a DropdownMenuRadioGroup. */
const DropdownMenuRadioItem = React.forwardRef(({
  className,
  children,
  description,
  label,
  icon,
  endComponent,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DropdownMenuPrimitive.RadioItem, {
  ref: ref,
  className: cn(menuStyleClasses.item({
    variant: "default"
  }), menuStyleClasses.inset, "group/dropdown-radio", className),
  ...props
}, /*#__PURE__*/React.createElement("span", {
  className: cn("absolute left-2", radioStyles())
}, /*#__PURE__*/React.createElement(DropdownMenuPrimitive.ItemIndicator, null, /*#__PURE__*/React.createElement("div", {
  className: radioIndicatorStyles()
}))), /*#__PURE__*/React.createElement(ItemWithLabelIconAndDescription, {
  label: label,
  icon: icon,
  description: description,
  endComponent: endComponent
}, children)));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;
/** Menu item rendered as a Chip tag, optionally removable. */
const DropdownMenuTagItem = React.forwardRef(({
  label,
  size = "xs",
  color = "primary",
  icon,
  onRemove,
  className,
  onClick,
  ...props
}, ref) => {
  return /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Item, {
    ref: ref,
    className: cn(menuStyleClasses.item({
      variant: "tags"
    }), className),
    ...props
  }, /*#__PURE__*/React.createElement(Chip, {
    label: label,
    size: size,
    color: color,
    onRemove: onRemove,
    onClick: onClick,
    icon: icon
  }));
});
DropdownMenuTagItem.displayName = "DropdownMenuTagItem";
/** Wrapping row layout for DropdownMenuTagItems. */
const DropdownMenuTagList = React.forwardRef(({
  children,
  className
}, ref) => {
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: cn("flex flex-wrap", className)
  }, children);
});
DropdownMenuTagList.displayName = "DropdownMenuTagList";

/** Non-interactive section heading inside the menu. */
const DropdownMenuLabel = React.forwardRef(({
  children,
  className,
  inset,
  label,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Label, {
  ref: ref,
  className: cn(menuStyleClasses.label, inset ? menuStyleClasses.inset : "", className),
  ...props
}, label && /*#__PURE__*/React.createElement(React.Fragment, null, label), children));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

/** Thin divider between groups of menu items. */
const DropdownMenuSeparator = React.forwardRef(({
  className,
  ...props
}, ref) => /*#__PURE__*/React.createElement(DropdownMenuPrimitive.Separator, {
  ref: ref,
  className: cn(menuStyleClasses.separator, className),
  ...props
}));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;
/** Right-aligned keyboard shortcut hint for a menu item. */
const DropdownMenuShortcut = ({
  className,
  shortcut,
  children,
  ...props
}) => {
  const resolvedShortcut = shortcut !== null && shortcut !== void 0 ? shortcut : "";
  if (!resolvedShortcut && children) {
    return /*#__PURE__*/React.createElement("span", {
      className: cn(className),
      ...props
    }, children);
  }
  return /*#__PURE__*/React.createElement(KeyboardShortcut, {
    shortcut: resolvedShortcut,
    className: cn(menuStyleClasses.shortcut, className),
    ...props
  });
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";
/** Search input header for a menu — use it for large, searchable datasets rather than an unbounded list; Enter activates the first result. */
const DropdownMenuSearchbar = React.forwardRef(({
  placeholder,
  value,
  onChange,
  onKeyDown,
  name,
  className,
  disabled = false,
  isLoading = false,
  button,
  autoFocus
}, ref) => {
  const internalRef = useRef(null);
  React.useImperativeHandle(ref, () => internalRef.current);
  React.useEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        var _internalRef$current;
        (_internalRef$current = internalRef.current) === null || _internalRef$current === void 0 || _internalRef$current.focus();
      }, 0);
    }
  }, [autoFocus]);
  const handleKeyDown = e => {
    e.stopPropagation();
    onKeyDown === null || onKeyDown === void 0 || onKeyDown(e);
    if (!e.defaultPrevented) {
      const menuContent = e.currentTarget.closest('[data-radix-menu-content][data-state="open"]');
      const firstItem = menuContent ? getFirstDropdownMenuItem(menuContent) : null;
      if (e.key === "Enter") {
        e.preventDefault();
        if (firstItem instanceof HTMLElement) {
          firstItem.click();
        }
      }
      if (e.key === "Tab" || e.key === "ArrowDown") {
        e.preventDefault();
        if (firstItem instanceof HTMLElement) {
          firstItem.focus();
        }
      }
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: cn("flex gap-1.5 p-1.5", className),
    "data-dropdown-searchbar": true
  }, /*#__PURE__*/React.createElement(SearchInput, {
    className: "w-full",
    ref: internalRef,
    placeholder: placeholder,
    name: name,
    value: value,
    onChange: onChange,
    onKeyDown: handleKeyDown,
    disabled: disabled,
    isLoading: isLoading
  }), button);
});
DropdownMenuSearchbar.displayName = "DropdownMenuSearchbar";
const DropdownMenuFiltersInner = ({
  filters,
  selectedValues = [],
  onSelectFilter,
  className
}, ref) => {
  const multiSelectionValues = Array.isArray(selectedValues) ? selectedValues : [];
  return /*#__PURE__*/React.createElement("div", {
    ref: ref,
    className: cn("flex flex-wrap gap-0.5 p-2", className)
  }, filters.map(filter => {
    const isSelected = multiSelectionValues.includes(filter.value);
    return /*#__PURE__*/React.createElement(Button, {
      key: filter.value,
      size: "xs",
      variant: isSelected ? "primary" : "outline",
      label: filter.label,
      onClick: () => onSelectFilter(filter.value)
    });
  }));
};

/** Row of toggleable filter buttons for narrowing the menu's items. */
const DropdownMenuFilters = React.forwardRef(DropdownMenuFiltersInner);
DropdownMenuFilters.displayName = "DropdownMenuFilters";

// DropdownTooltip: Simple tooltip with consistent layout: optional media at top, description below.

const DropdownTooltip = ({
  description,
  media
}) => /*#__PURE__*/React.createElement("div", {
  className: "space-y-4"
}, media && /*#__PURE__*/React.createElement("div", {
  className: "rounded-sm"
}, media), /*#__PURE__*/React.createElement("p", {
  className: "text-foreground text-sm font-normal"
}, description));
DropdownTooltip.displayName = "DropdownTooltip";
/** Wraps a menu item to show a rich tooltip (media + description) beside it on hover. */
const DropdownTooltipTrigger = React.forwardRef(({
  children,
  className,
  description,
  media,
  mountPortal = true,
  mountPortalContainer,
  onVisibilityChange,
  side = "right",
  sideOffset = 8
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const handleOpenChange = React.useCallback(open => {
    setIsOpen(open);
    onVisibilityChange === null || onVisibilityChange === void 0 || onVisibilityChange(open);
  }, [onVisibilityChange]);
  React.useEffect(() => {
    var _window$visualViewpor;
    if (!isOpen) {
      return;
    }
    const closeTooltip = () => {
      handleOpenChange(false);
    };
    window.addEventListener("scroll", closeTooltip, true);
    (_window$visualViewpor = window.visualViewport) === null || _window$visualViewpor === void 0 || _window$visualViewpor.addEventListener("scroll", closeTooltip);
    return () => {
      var _window$visualViewpor2;
      window.removeEventListener("scroll", closeTooltip, true);
      (_window$visualViewpor2 = window.visualViewport) === null || _window$visualViewpor2 === void 0 || _window$visualViewpor2.removeEventListener("scroll", closeTooltip);
    };
  }, [handleOpenChange, isOpen]);
  const tooltipContent = /*#__PURE__*/React.createElement(TooltipPrimitive.Content, {
    side: side,
    sideOffset: sideOffset,
    className: cn(menuStyleClasses.container, menuStyleClasses.containerAnimation, "w-48 max-w-sm p-2 shadow-lg")
  }, /*#__PURE__*/React.createElement(DropdownTooltip, {
    description: description,
    media: media
  }));
  const container = useSheetContainer(mountPortalContainer);
  return /*#__PURE__*/React.createElement(TooltipPrimitive.Provider, {
    delayDuration: 700
  }, /*#__PURE__*/React.createElement(TooltipPrimitive.Root, {
    open: isOpen,
    onOpenChange: handleOpenChange
  }, /*#__PURE__*/React.createElement(TooltipPrimitive.Trigger, {
    asChild: true,
    className: className,
    ref: ref
  }, /*#__PURE__*/React.createElement("span", {
    className: "block w-full"
  }, children)), mountPortal ? /*#__PURE__*/React.createElement(TooltipPrimitive.Portal, {
    container: container
  }, tooltipContent) : tooltipContent));
});
DropdownTooltipTrigger.displayName = "DropdownTooltipTrigger";
/** Non-interactive label/value row inside the menu (e.g. usage or status info). */
const DropdownMenuStaticItem = React.forwardRef(({
  label,
  value,
  children,
  className
}, ref) => /*#__PURE__*/React.createElement("div", {
  ref: ref,
  className: cn("flex h-9 items-center gap-2 px-2 text-sm", "text-foreground", className)
}, /*#__PURE__*/React.createElement("span", {
  className: "grow font-medium"
}, label), value && /*#__PURE__*/React.createElement("span", {
  className: cn("shrink-0", "text-muted-foreground")
}, value), children && /*#__PURE__*/React.createElement("div", {
  className: "shrink-0"
}, children)));
DropdownMenuStaticItem.displayName = "DropdownMenuStaticItem";
export { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuFilters, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSearchbar, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuStaticItem, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTagItem, DropdownMenuTagList, DropdownMenuTrigger, DropdownTooltipTrigger };
//# sourceMappingURL=Dropdown.js.map